
import React, { useState } from 'react';
import { FileText, CheckCircle, Clock, AlertCircle, Printer, ChevronDown } from 'lucide-react';
import { useAppContext } from '../../App.jsx';
import { useNavigate } from 'react-router-dom';

const InvoiceMessage = ({ message, activeRole, chatInfo, user, chatId }) => {
    const { processPayment } = useAppContext();
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const invoice = message.invoice;
    const isPaid = invoice.status === 'Pago';
    const isClient = activeRole === 'client';

    const handleConfirmPayment = async () => {
        if (!isClient || isPaid) return;
        try {
            await processPayment(chatId, message.id);
        } catch (error) {
            console.error(error);
        }
    };

    const handlePrint = () => {
        // Adaptamos os dados da fatura para o formato que a tela de impressão espera
        const printData = {
            quote: {
                ...invoice,
                items: [
                    ...(invoice.includedProposals?.flatMap(p => p.items) || []),
                    ...(invoice.newItems || [])
                ]
            }
        };
        navigate('/print-quote', { state: { message: printData, chatInfo } });
    };

    return (
        <div className={`flex flex-col w-full max-w-sm my-4 animate-slide-up ${message.senderId === user.uid ? 'ml-auto items-end' : 'mr-auto items-start'}`}>
            <div className="bg-white rounded-[2rem] shadow-xl overflow-hidden border-2 border-blue-50 w-full">

                {/* Header da Fatura */}
                <div className="bg-blue-600 p-5 text-white flex justify-between items-center relative overflow-hidden">
                    <div className="flex items-center relative z-10">
                        <FileText className="mr-3" size={24} />
                        <div>
                            <h3 className="font-black uppercase tracking-tighter text-sm">Fatura de Serviço</h3>
                            <p className="text-[10px] opacity-80 font-bold uppercase tracking-widest">Detalhamento Oficial</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 relative z-10">
                        <button
                            onClick={handlePrint}
                            className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all active:scale-90"
                            title="Imprimir Comprovante"
                        >
                            <Printer size={18} />
                        </button>
                        {isPaid ? <CheckCircle size={24} className="text-emerald-300" /> : <Clock size={24} className="text-blue-200 animate-pulse" />}
                    </div>
                    {/* Efeito de brilho no fundo */}
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
                </div>

                <div className="p-6 space-y-4">

                    {/* Botão de Ver Detalhes / Retrair */}
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-full flex items-center justify-between py-2 px-1 border-b border-gray-100 group"
                    >
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover:text-blue-600 transition-colors">
                            {isExpanded ? 'Ocultar Detalhes' : 'Ver Detalhes dos Itens'}
                        </span>
                        <ChevronDown size={14} className={`text-gray-400 group-hover:text-blue-600 transition-all duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Lista de Itens (Expansível) */}
                    {isExpanded && (
                        <div className="space-y-3 pt-1 animate-fade-in">
                            {invoice.includedProposals?.map((proposal, pIdx) => (
                                <React.Fragment key={`prop-${pIdx}`}>
                                    {proposal.items?.map((item, iIdx) => (
                                        <div key={`item-${pIdx}-${iIdx}`} className="flex justify-between items-start text-sm">
                                            <span className="text-gray-700 font-bold flex-1 mr-2">{item.description}</span>
                                            <span className="font-black text-gray-900 whitespace-nowrap text-xs">R$ {parseFloat(item.price).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </React.Fragment>
                            ))}

                            {invoice.newItems?.map((item, idx) => (
                                <div key={`new-${idx}`} className="flex justify-between items-start text-sm italic border-l-2 border-blue-100 pl-2">
                                    <span className="text-gray-600 font-medium flex-1 mr-2">{item.description} (Extra)</span>
                                    <span className="font-bold text-gray-800 whitespace-nowrap text-xs">R$ {parseFloat(item.price).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Aviso de Itens Não Cobrados */}
                    {invoice.skippedProposals && invoice.skippedProposals.length > 0 && (
                        <div className="bg-amber-50 p-3 rounded-xl border border-amber-100 flex items-start mt-4">
                            <AlertCircle size={14} className="text-amber-600 mr-2 mt-0.5 shrink-0" />
                            <div className="text-[10px] text-amber-800 leading-tight">
                                <p className="font-bold uppercase tracking-tighter mb-1">Atenção</p>
                                Orçamentos pendentes não incluídos: {invoice.skippedProposals.join(', ')}
                            </div>
                        </div>
                    )}

                    {/* Resumo Financeiro */}
                    <div className="bg-gray-50 p-4 rounded-2xl space-y-2 border border-gray-100 mt-2">
                        {invoice.discount > 0 && (
                            <div className="flex justify-between text-[10px] font-black text-red-500 uppercase">
                                <span>Desconto</span>
                                <span>- R$ {parseFloat(invoice.discount).toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                            <span className="font-black text-gray-400 uppercase text-[10px] tracking-widest">Total Líquido</span>
                            <span className="text-2xl font-black text-gray-900 leading-none">R$ {parseFloat(invoice.total).toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Botão de Ação / Status */}
                    <div className="mt-4">
                        {isClient && !isPaid ? (
                            <button
                                onClick={handleConfirmPayment}
                                className="w-full py-4 bg-green-600 text-white font-black rounded-2xl shadow-lg shadow-green-100 hover:bg-green-700 transition-all active:scale-95 uppercase text-xs tracking-widest"
                            >
                                Confirmar Pagamento
                            </button>
                        ) : isPaid ? (
                            <div className="flex items-center justify-center p-3 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-100 font-black text-[10px] uppercase tracking-[0.2em]">
                                <CheckCircle size={14} className="mr-2" /> Pagamento Concluído
                            </div>
                        ) : (
                            <div className="text-center p-3 bg-blue-50 text-blue-600 rounded-xl font-bold text-[10px] uppercase tracking-widest">
                                Aguardando Pagamento do Cliente
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceMessage;
